import { memo } from 'react';
import type { TeamShapeBand } from '@4cm/core/tactics/overlays';

/**
 * Sky-MNF "horizontal lines" overlay — connect each team's players
 * within a back / mid / front Z-band with a polyline so the team's
 * shape reads at a glance.
 *
 * Coordinate convention: world `{x, z}` → SVG `(-z, x)` per the
 * landscape mapping shared by every other 2D layer.
 *
 * Two strokes per band: a darker drop-shadow underneath + the team-
 * coloured stroke on top, so the lines pop against the grass even at
 * low opacity.
 */

export interface TeamShapeLayer2DProps {
  bands: ReadonlyArray<TeamShapeBand>;
  /** Opacity in [0, 1]. */
  opacity: number;
  /** Hide away bands when false (master opp toggle). Default true. */
  showAway?: boolean;
}

const HOME_HEX = '#4ade80';
const AWAY_HEX = '#fbbf24';

function strokeFor(team: 'home' | 'away', opacity: number): string {
  if (team === 'away') return `rgba(251, 191, 36, ${opacity})`;
  return `rgba(74, 222, 128, ${opacity})`;
}

function TeamShapeLayer2DInner({
  bands,
  opacity,
  showAway = true,
}: TeamShapeLayer2DProps) {
  if (!bands || bands.length === 0) return null;
  return (
    <g
      className="t2d-team-shape-layer"
      data-testid="t2d-team-shape-layer"
      pointerEvents="none"
      clipPath="url(#t2d-pitch-clip)"
    >
      {bands.map((band, idx) => {
        if (!showAway && band.team === 'away') return null;
        // World (x, z) → SVG (-z, x) per the landscape mapping.
        const points = band.points
          .map((p) => `${-p.z},${p.x}`)
          .join(' ');
        return (
          <polyline
            key={idx}
            points={points}
            fill="none"
            stroke={strokeFor(band.team, opacity)}
            strokeWidth={0.55}
            strokeLinecap="round"
            strokeLinejoin="round"
            data-team={band.team}
            data-testid={`t2d-team-shape-band-${idx}`}
          />
        );
      })}
    </g>
  );
}

export const TeamShapeLayer2D = memo(TeamShapeLayer2DInner);

/** Re-export the colour hexes so the 3D layer reads them from one
 *  place rather than duplicating the constants. */
export const TEAM_SHAPE_HOME_HEX = HOME_HEX;
export const TEAM_SHAPE_AWAY_HEX = AWAY_HEX;
